import Link from "next/link";
import Image from "next/image";
import { ArrowUpLeft } from "lucide-react";
import type { Product } from "@/lib/types";
import { money, number } from "@/lib/config";
import { QuickAdd } from "./quick-add";
import { quickAddSnapshot, type ProductCardProduct } from "@/lib/product-card-data";
import { shouldSkipProductImageOptimization } from "@/lib/product-image-url";
export { productCardData } from "@/lib/product-card-data";
export type { ProductCardProduct } from "@/lib/product-card-data";

function ProductCardPhoto({ src, hoverSrc, alt, sizes }: { src: string; hoverSrc?: string; alt: string; sizes: string }) {
  return (
    <>
      <Image src={src} alt={alt} width={800} height={800} sizes={sizes} quality={85} unoptimized={shouldSkipProductImageOptimization(src)} className="product-photo-content" />
      {hoverSrc && hoverSrc !== src && (
        <Image
          src={hoverSrc}
          alt=""
          width={800}
          height={800}
          sizes={sizes}
          quality={85}
          unoptimized={shouldSkipProductImageOptimization(hoverSrc)}
          className="product-photo-content product-photo-hover product-photo-hover-static"
        />
      )}
    </>
  );
}

export function ProductCard({ product, hoverImage = false, quickAddProduct, imageSizes = "(max-width: 640px) 50vw, (max-width: 1000px) 33vw, 25vw", marqueeClone = false }: { product: ProductCardProduct; hoverImage?: boolean; quickAddProduct?: Product; imageSizes?: string; marqueeClone?: boolean }) {
  const displayPrice = product.cardPrice ?? product.basePrice;
  const hasDiscount = !product.demo && Number.isFinite(displayPrice) && Number.isFinite(product.oldPrice)
    && Number(product.oldPrice) > Number(displayPrice) && Number(displayPrice) > 0;
  const discountPercent = hasDiscount ? Math.round((Number(product.oldPrice) - Number(displayPrice)) / Number(product.oldPrice) * 100) : 0;
  const subtitle = product.subtitle.trim().startsWith("از دسته") ? "" : product.subtitle;
  return (
    <article className="product-card">
      <Link href={`/products/${product.slug}`} className="product-picture" prefetch={false} tabIndex={marqueeClone ? -1 : undefined}>
        <ProductCardPhoto src={product.images[0]} hoverSrc={hoverImage ? product.images[1] : undefined} alt={product.name} sizes={imageSizes} />
        <span className="product-badge">
          {product.demo
            ? "تصویر محصول"
            : product.hasMadeToOrder
              ? "ساخت سفارشی"
              : "مشاهدهٔ محصول"}
        </span>
        {hasDiscount && <span className="product-discount-badge">{number(discountPercent)}٪ تخفیف</span>}
        <span className="product-go">
          <ArrowUpLeft size={22} />
        </span>
      </Link>
      <div className="product-meta">
        <span>
          {product.material === "PLA"
            ? "زیست‌پلاستیک پی‌ال‌ای"
            : product.material === "PETG"
              ? "پلیمر پی‌ای‌تی‌جی"
              : product.material}
        </span>
        <div className="swatches" role="group" aria-label="رنگ‌های موجود">
          {product.swatches.map((v) => (
            <span key={v.id} title={v.color} style={{ background: v.hex }} />
          ))}
        </div>
      </div>
      <Link href={`/products/${product.slug}`} prefetch={false} tabIndex={marqueeClone ? -1 : undefined}>
        <h3>{product.name}</h3>
      </Link>
      {subtitle && <p className="product-subtitle">{subtitle}</p>}
      <div className="product-price">
        <span className="product-price-amounts">
          {product.demo
            ? "قیمت ثبت نشده"
            : displayPrice && displayPrice > 0
              ? <><span className="product-price-current">{money(displayPrice)}</span>{hasDiscount && <del className="product-price-old">{money(Number(product.oldPrice))}</del>}</>
              : "قیمت اعلام نشده"}
        </span>
        {!product.demo && <QuickAdd product={quickAddProduct ? quickAddSnapshot(quickAddProduct) : product.quickAddData} tabIndex={marqueeClone ? -1 : undefined} />}
      </div>
    </article>
  );
}
